import {
  type AssessmentSlideOverCandidate,
  useAssessmentSlideOver,
} from "@components/campaigns/AssessmentSlideOver";
import { CandidateAssessmentSummary } from "@components/campaigns/CandidateAssessmentSummary";
import {
  LatestRoundOutcomeCard,
  type LatestSynthesisVersionData,
} from "@components/campaigns/LatestRoundOutcomeCard";
import { OutreachDisclaimer } from "@components/campaigns/outreach/OutreachDisclaimer";
import { OutreachTimeline } from "@components/campaigns/stages/OutreachTimeline";
import { OpenFullProfileLink } from "@components/candidates/OpenFullProfileLink";
import { ExportButton } from "@components/exports/ExportButton";
import { EXPORT_CANDIDATE_REPORT_TO_GOOGLE_DOCS } from "@lib/graphql/mutations";
import { CAMPAIGN_CANDIDATE_DETAIL_QUERY } from "@lib/graphql/queries";
import { stepDisplayLabel } from "@lib/selectionSteps";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";

interface CandidateDetailPanelProps {
  campaignCandidateId: string;
  candidateId?: string;
  candidateName: string;
  candidateRole?: string;
  candidateCompany?: string;
}

interface BlueprintDimensionData {
  id: string;
  continuityId: string;
  name: string;
  idealScore: number | null;
}

interface NoteData {
  id: string;
  content: string;
  noteType: string;
  author: { id: string; name: string };
  createdAt: string;
}

interface OutreachAttemptData {
  id: string;
  channel: string;
  direction: string;
  status: string;
  messageContent: string | null;
  responseContent: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  sentBy: { id: string; name: string };
  createdAt: string;
  createdVia?: string;
}

interface OutreachDraftData {
  id: string;
  kind: string;
  currentVersion: {
    id: string;
    versionNumber: number;
    origin: string;
    generationState: string;
    messages: { label: string; body: string }[];
  } | null;
}

interface InterviewContributionData {
  id: string;
  kind: string;
  candidateSelectionStepId: string | null;
  occurredAt: string | null;
  createdAt: string;
  contributor: { id: string; name: string };
  note: {
    interviewType: string;
    rawNotes: string | null;
    durationMinutes: number | null;
    recordingNotes: string | null;
  } | null;
  transcript: {
    content: string | null;
    source: string | null;
    originalFilename: string | null;
    extractionState: string | null;
  } | null;
  assessmentFormSubmission: { recommendation: string | null; summary: string | null } | null;
}

interface SelectionStepData {
  id: string;
  round: number | null;
  status: string;
  outcome: string;
  rating: number | null;
  stepType: string | null;
  label: string | null;
  scheduledAt: string | null;
  interviewers: string | null;
  clientFeedback: string | null;
  competencyFeedback: unknown;
  createdAt: string;
}

type AssessmentData = NonNullable<AssessmentSlideOverCandidate["candidateAssessment"]> & {
  id: string;
};

interface OfferData {
  id: string;
  status: string;
  baseSalary: number | null;
  currency: string | null;
  startDate: string | null;
  createdAt: string;
}

interface PortalCommentData {
  id: string;
  content: string;
  rating: number | null;
  authorName: string;
  createdAt: string;
}

interface CampaignCandidateDetail {
  id: string;
  campaign: {
    id: string;
    roleBlueprint: { id: string; capabilityDimensions: BlueprintDimensionData[] } | null;
  };
  attachedNotes: NoteData[];
  outreachAttempts: OutreachAttemptData[];
  outreachDrafts?: OutreachDraftData[];
  interviewContributions: InterviewContributionData[];
  candidateSelectionSteps: SelectionStepData[];
  candidateAssessment: AssessmentData | null;
  latestSynthesisVersion: LatestSynthesisVersionData | null;
  offers: OfferData[];
  portalComments: PortalCommentData[];
}

const NOTE_TYPE_LABELS: Record<string, string> = {
  general: "General",
  ai_generated: "AI Generated",
  call_summary: "Call Summary",
  feedback: "Feedback",
};

const INTERVIEW_TYPE_LABELS: Record<string, string> = {
  screening: "Screening",
  competency: "Competency",
  technical: "Technical",
  cultural: "Cultural",
  final: "Final",
};

const OUTCOME_LABELS: Record<string, string> = {
  pending: "Pending",
  advance: "Advance",
  reject: "Reject",
  hold: "Hold",
};

const OUTCOME_COLORS: Record<string, string> = {
  pending: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  advance: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  reject: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  hold: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
      {title} ({count})
    </h4>
  );
}

const DRAFT_KIND_LABELS: Record<string, string> = {
  email: "Email",
  linkedin_message: "LinkedIn message",
  linkedin_connection_request: "LinkedIn connection request",
};

// Outreach drafts (CandidateOutreachMessage) — distinct from the contact log.
// Nothing here has been sent; the link opens the Outreach workspace with this
// candidate and channel selected (#3169).
function OutreachDraftsSection({
  drafts,
  campaignId,
  ccId,
}: {
  drafts: OutreachDraftData[];
  campaignId: string;
  ccId: string;
}) {
  return (
    <div>
      <SectionHeader title="Drafts" count={drafts.length} />
      <div className="space-y-2">
        {drafts.map((draft) => {
          const version = draft.currentVersion;
          const firstLine =
            version?.messages.find((m) => m.body.trim().length > 0)?.body.split("\n")[0] ?? "";
          return (
            <div key={draft.id} className="rounded border border-border p-2">
              <div className="flex flex-wrap items-center gap-2 text-[10px] text-text-faint">
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium dark:bg-gray-800">
                  {DRAFT_KIND_LABELS[draft.kind] ?? draft.kind}
                </span>
                {version && <span>v{version.versionNumber}</span>}
                {version && (
                  <span
                    className={
                      version.origin === "user"
                        ? "rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                        : "rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    }
                  >
                    {version.origin === "user" ? "Edited" : "AI"}
                  </span>
                )}
                {version?.generationState === "generating" && <span>Generating…</span>}
                <Link
                  to={`/campaigns/${campaignId}/run/outreach?cc=${ccId}&channel=${draft.kind}`}
                  className="ml-auto text-accent hover:underline"
                >
                  Open draft
                </Link>
              </div>
              {firstLine && (
                <p className="mt-1 line-clamp-2 text-xs text-text-secondary">{firstLine}</p>
              )}
            </div>
          );
        })}
      </div>
      <OutreachDisclaimer campaignId={campaignId} className="mt-2" />
    </div>
  );
}

function NotesSection({ notes }: { notes: NoteData[] }) {
  return (
    <div>
      <SectionHeader title="Notes" count={notes.length} />
      <div className="space-y-2">
        {notes.map((note) => (
          <div key={note.id} className="rounded border border-border p-2">
            <div className="flex items-center gap-2 text-[10px] text-text-faint">
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium dark:bg-gray-800">
                {NOTE_TYPE_LABELS[note.noteType] ?? note.noteType}
              </span>
              <span>{note.author.name}</span>
              <span>&middot;</span>
              <span>{new Date(note.createdAt).toLocaleDateString()}</span>
            </div>
            <p className="mt-1 line-clamp-3 text-xs text-text-secondary">{note.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const CONTRIBUTION_KIND_LABELS: Record<string, string> = {
  note: "Note",
  transcript: "Transcript",
  assessment_form_submission: "Assessment Form",
};

function stripHtmlTags(html: string): string {
  return html
    .replace(/<\/(p|div|h[1-6]|li|tr|br)>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function InterviewContributionsSection({
  contributions,
}: {
  contributions: InterviewContributionData[];
}) {
  return (
    <div>
      <SectionHeader title="Interview Feedback" count={contributions.length} />
      <div className="space-y-2">
        {contributions.map((c) => {
          const date = c.occurredAt ?? c.createdAt;
          const typeLabel =
            c.kind === "note" && c.note
              ? (INTERVIEW_TYPE_LABELS[c.note.interviewType] ?? c.note.interviewType)
              : (CONTRIBUTION_KIND_LABELS[c.kind] ?? c.kind);
          const rawBody =
            c.note?.rawNotes ??
            c.transcript?.content ??
            c.assessmentFormSubmission?.summary ??
            null;
          const body = rawBody ? stripHtmlTags(rawBody) : null;
          return (
            <div key={c.id} className="rounded border border-border p-2">
              <div className="flex items-center gap-2 text-[10px] text-text-faint">
                <span className="rounded bg-blue-100 px-1.5 py-0.5 font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {typeLabel}
                </span>
                <span>{c.contributor.name}</span>
                {date && (
                  <>
                    <span>&middot;</span>
                    <span>{new Date(date).toLocaleDateString()}</span>
                  </>
                )}
                {c.note?.durationMinutes && (
                  <>
                    <span>&middot;</span>
                    <span>{c.note.durationMinutes} min</span>
                  </>
                )}
              </div>
              {body && <p className="mt-1 line-clamp-3 text-xs text-text-secondary">{body}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ClientInterviewsSection({ steps }: { steps: SelectionStepData[] }) {
  return (
    <div>
      <SectionHeader title="Client Interviews" count={steps.length} />
      <div className="space-y-2">
        {steps.map((step) => (
          <div key={step.id} className="rounded border border-border p-2">
            <div className="flex items-center gap-2 text-[10px] text-text-faint">
              <span className="font-medium text-text-secondary">{stepDisplayLabel(step)}</span>
              <span
                className={`rounded-full px-1.5 py-0.5 font-medium ${OUTCOME_COLORS[step.outcome] ?? OUTCOME_COLORS.pending}`}
              >
                {OUTCOME_LABELS[step.outcome] ?? step.outcome}
              </span>
              {step.rating && <span className="text-text-secondary">{step.rating}/5</span>}
              {step.scheduledAt && (
                <>
                  <span>&middot;</span>
                  <span>{new Date(step.scheduledAt).toLocaleDateString()}</span>
                </>
              )}
            </div>
            {step.clientFeedback && (
              <p className="mt-1 line-clamp-3 text-xs text-text-secondary">{step.clientFeedback}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function OffersSection({ offers }: { offers: OfferData[] }) {
  return (
    <div>
      <SectionHeader title="Offers" count={offers.length} />
      <div className="space-y-2">
        {offers.map((offer) => (
          <div key={offer.id} className="rounded border border-border p-2">
            <div className="flex items-center gap-2 text-[10px] text-text-faint">
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium dark:bg-gray-800">
                {offer.status.replace("_", " ")}
              </span>
              {offer.baseSalary != null && (
                <span className="text-text-secondary">
                  {offer.currency ?? ""} {offer.baseSalary.toLocaleString()}
                </span>
              )}
              {offer.startDate && (
                <>
                  <span>&middot;</span>
                  <span>Start: {new Date(offer.startDate).toLocaleDateString()}</span>
                </>
              )}
              <span>&middot;</span>
              <span>{new Date(offer.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortalFeedbackSection({ comments }: { comments: PortalCommentData[] }) {
  return (
    <div>
      <SectionHeader title="Portal Feedback" count={comments.length} />
      <div className="space-y-2">
        {comments.map((comment) => (
          <div key={comment.id} className="rounded border border-border p-2">
            <div className="flex items-center gap-2 text-[10px] text-text-faint">
              <span className="rounded bg-indigo-100 px-1.5 py-0.5 font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                Client Feedback
              </span>
              <span>{comment.authorName}</span>
              {comment.rating != null && <span>{comment.rating}/5</span>}
              <span>&middot;</span>
              <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
            </div>
            <p className="mt-1 line-clamp-3 text-xs text-text-secondary">{comment.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CandidateDetailPanel({
  campaignCandidateId,
  candidateId,
  candidateName,
  candidateRole,
  candidateCompany,
}: CandidateDetailPanelProps) {
  const [{ data, fetching }] = useQuery({
    query: CAMPAIGN_CANDIDATE_DETAIL_QUERY,
    variables: { id: campaignCandidateId },
  });
  const [, exportCandidateReport] = useMutation(EXPORT_CANDIDATE_REPORT_TO_GOOGLE_DOCS);

  const cc: CampaignCandidateDetail | null = data?.campaignCandidate ?? null;

  const dimensions = cc?.campaign.roleBlueprint?.capabilityDimensions ?? [];
  const slideOverCandidate: AssessmentSlideOverCandidate = {
    id: campaignCandidateId,
    candidate: {
      fullName: candidateName,
      currentRoleTitle: candidateRole ?? null,
      currentCompany: candidateCompany ?? null,
      location: null,
    },
    candidateAssessment: cc?.candidateAssessment ?? null,
  };
  const { open: openAssessment, slideOverElement: assessmentSlideOver } = useAssessmentSlideOver({
    candidates: [slideOverCandidate],
    dimensions,
  });

  if (fetching) {
    return <p className="py-4 text-center text-xs text-text-muted">Loading candidate details...</p>;
  }

  if (!cc) {
    return <p className="py-4 text-center text-xs text-text-muted">Could not load details.</p>;
  }

  async function startCandidateExport() {
    const result = await exportCandidateReport({ input: { campaignCandidateId } });
    const payload = result.data?.exportCandidateReportToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  }

  const hasNotes = cc.attachedNotes.length > 0;
  const outreachDrafts = cc.outreachDrafts ?? [];
  const hasDrafts = outreachDrafts.length > 0;
  const hasOutreach = cc.outreachAttempts.length > 0;
  const hasInterviewContributions = cc.interviewContributions.length > 0;
  // Only steps that represent an actual interview record (scheduled or decided),
  // mirroring the old ClientInterview rows; pending future steps are omitted.
  const interviewSteps = cc.candidateSelectionSteps.filter((s) => s.status !== "pending");
  const hasClientInterviews = interviewSteps.length > 0;
  const hasAssessment = cc.candidateAssessment?.currentVersion != null;
  const hasLatestSynthesis = cc.latestSynthesisVersion != null;
  const hasOffers = cc.offers.length > 0;
  const hasPortalComments = cc.portalComments.length > 0;
  const hasAny =
    hasNotes ||
    hasDrafts ||
    hasOutreach ||
    hasInterviewContributions ||
    hasClientInterviews ||
    hasAssessment ||
    hasLatestSynthesis ||
    hasOffers ||
    hasPortalComments;

  return (
    <div className="p-4">
      <div
        className={
          hasAny ? "mb-4 flex items-center justify-between" : "flex items-center justify-between"
        }
      >
        {candidateId ? (
          <OpenFullProfileLink candidateId={candidateId} variant="header" />
        ) : (
          <span />
        )}
        <ExportButton
          onStart={startCandidateExport}
          className="flex items-center gap-1.5 rounded border border-input-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
        />
      </div>
      {hasAny ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {hasNotes && <NotesSection notes={cc.attachedNotes} />}
          {hasDrafts && (
            <OutreachDraftsSection
              drafts={outreachDrafts}
              campaignId={cc.campaign.id}
              ccId={cc.id}
            />
          )}
          {hasOutreach && (
            <div>
              <SectionHeader title="Contact log" count={cc.outreachAttempts.length} />
              <OutreachTimeline attempts={cc.outreachAttempts} campaignId={cc.campaign.id} />
              <OutreachDisclaimer campaignId={cc.campaign.id} className="mt-2" />
            </div>
          )}
          {hasInterviewContributions && (
            <InterviewContributionsSection contributions={cc.interviewContributions} />
          )}
          {hasClientInterviews && <ClientInterviewsSection steps={interviewSteps} />}
          {hasAssessment && (
            <CandidateAssessmentSummary
              assessment={cc.candidateAssessment!.currentVersion!}
              candidateName={candidateName}
              dimensions={cc.campaign.roleBlueprint?.capabilityDimensions}
              onViewDetails={() => openAssessment(campaignCandidateId)}
            />
          )}
          {hasLatestSynthesis && <LatestRoundOutcomeCard synthesis={cc.latestSynthesisVersion} />}
          {hasOffers && <OffersSection offers={cc.offers} />}
          {hasPortalComments && <PortalFeedbackSection comments={cc.portalComments} />}
        </div>
      ) : (
        <p className="py-4 text-center text-xs text-text-muted">No history recorded yet.</p>
      )}
      {assessmentSlideOver}
    </div>
  );
}
